/**
 * UndoRedo History Entry base class
 *
 * This class will be extended by each HistoryEntry types
 *
 *      when you overwrite the methods, pay attention to the return of the functions
 *      to match the same type described here
 */
define(
    "diy/undo-redo/historyEntry/Base",
    [
        'diy/undo-redo/constants',
        'diy/util/UUID'
    ],
    /**
     *
     * @param {module.undoRedo.constants} constants
     * @param {diy.util.UUID} UUID
     * @returns {*}
     */
    function (constants, UUID) {
        'use strict';

        var
            manager = 'entries',

            /**
             * this method is dummy; it should be extended by each HistoryEntry type class to return the same value types
             */
            defaultMethod = function (result) {
            },
            /**
             * this method is dummy; it should be extended by each HistoryEntry type class to return the same value types
             *
             * @returns {promise|*|Q.promise}
             */
            defaultPromiseMethod = function (resultData) {
                var deferred = Q.defer();
                deferred.resolve(resultData);
                return deferred.promise;
            },

            HistoryEntry;

        /**
         * @class module.undoRedo.HistoryEntry.Base
         * @template module.undoRedo.HistoryEntry.Base
         * @lends {module.undoRedo.HistoryEntry.Base.prototype}
         * @constructor
         */
        HistoryEntry = Klazz.extend({
            /** @type {module.undoRedo.Api} */
            api: null,

            /**
             * @var {String} - can be 'client' or 'server' depending where is this entry executed
             */
            executionType: constants.executionType.CLIENT,

            /**
             * @var {String} - can be 'undo' or 'redo' depending of the chain
             */
            entryType: constants.types.UNDO,
            /**
             * @var {String} - uuid generated for client entries | id for server side entries
             */
            key: null,

            /**
             * @var {String} - undo session in witch it was created
             */
            undoSessionId: null,

            /**
             * @var {String} entry position in session (informational, not used by engine)
             */
            undoSessionIndex: 0,

            /**
             * @var {Number} - server side id used in server executed entries
             */
            entryId: 0,

            /**
             * represents the class name / instance type; will be for serialization
             */
            /** @type {?String} */
            manager: '',

            /**
             * @var (boolean) - defines if entry is executable or not
             */
            executable: true,

            /**
             * @var {Object} - payload for current entry
             */
            payload: undefined,

            /**
             * @var {String} - message for current entry
             */
            message: '',

            /**
             * Called to identify entry type
             *
             * @returns {String}
             */
            getManager: function () {
                return manager;
            },

            /**
             *
             * @param {String} string
             */
            setManager: function (string) {
                this.manager = string;
                return this;
            },

            /**
             * @param {?Object=} payload - class can be constructed with an empty payload
             * @param {module.undoRedo.Api} api
             */
            initialize: function (payload, api) {
                this.key = UUID.generate();
                if (payload && Object.keys(payload).length !== 0) {
                    this.payload = payload;
                } else {
                    this.payload = {};
                }
                this.api = api;
            },

            /**
             * Called to serialize current entry into an object from witch it can self restore using this.unserialize
             *      - do not include message in returned object
             *
             * @returns Object
             */
            serialize: function () {
                return {};
            },

            /**
             * Called to re-state the class to the state from before this.serialize
             *
             * @param {Object} serializedObject
             */
            unserialize: function (serializedObject) {
                return this;
            },

            /**
             * Called when user select this entry to be executed (ui related)
             */
            onSelect: defaultMethod,

            /**
             * Called en user deselect this entry (ui related)
             */
            onDeselect: defaultMethod,

//        /* can be implemented if required */
//        onInsert: defaultMethod,
//        onDelete: defaultMethod,

            /**
             * Called to create a snapshot for the current state
             * returns promise.resolve({snapshot: {Object}, message: string})
             *
             *
             * @returns {Promise|Q.promise}
             */
            createSnapshot: function () {
                throw "createSnapshot is called but not implemented";
            },

            /**
             * Called when a client side entry is executed
             *  - methods MUST not break the execution chain (can not do context change / page refresh)
             *
             * @returns {Promise|Q.promise}
             */
            execute: function () {
                throw "execute is called but not implemented";
            },

            /**
             * Applies on client side BUT does not perform save on server (on client side entries)
             *
             * @returns {Promise|Q.promise}
             */
            applySnapshot: defaultPromiseMethod,
            /**
             * Saves data from client on server  (on client side entries)
             *
             * @returns {Promise|Q.promise}
             */
            submit: defaultPromiseMethod,
            /**
             * Called (for server side entries only) after the entry was executed
             *  - method MUST not break the execution chain (can not do context change / page refresh)
             *
             * @param executionDetails Object
             * @returns {Promise|Q.promise}
             */
            onExecuted: defaultPromiseMethod,

            /**
             * Called when a client | server side entry is executed by user request
             *      (when is the entry that user clicked on / last entry in the execution chain)
             *      - onExecuted | execute was called before this one gets called
             *      - if you need to break the chain / refresh the page, this is the method you need
             */
            onExecutedLast: defaultMethod,


            /* Setters & Getters Below */

            /**
             * Setter function
             *
             *
             * @param isExecutable
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setExecutable: function (isExecutable) {
                if (isExecutable === true || isExecutable === 1) {
                    this.executable = true;
                } else {
                    this.executable = false;
                }
                return this;
            },

            /**
             * Getter function
             *
             * @returns {boolean}
             */
            getExecutable: function () {
                return this.executable;
            },

            /**
             * Setter function
             *
             * @param {String} key
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setKey: function (key) {
                this.key = key;
                return this;
            },

            /**
             * Getter function
             *
             * @returns {String}
             */
            getKey: function () {
                return this.key;
            },

            /**
             * Setter function
             *
             * @param id
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setId: function (id) {
                this.entryId = id;
                return this;
            },

            /**
             * Getter function
             *
             * @returns {Number|null}
             */
            getId: function () {
                return this.entryId;
            },

            /**
             * Setter function
             *
             * @param undoSessionId
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setUndoSessionId: function (undoSessionId) {
                this.undoSessionId = undoSessionId;
                return this;
            },

            /**
             * Getter function
             *
             * @returns {String}
             */
            getUndoSessionId: function () {
                return this.undoSessionId;
            },

            /**
             * Setter function
             *
             * @param undoSessionIndex
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setSessionIndex: function (undoSessionIndex) {
                this.undoSessionIndex = undoSessionIndex;
                return this;
            },

            /**
             * Getter function
             *
             * @returns {number}
             */
            getSessionIndex: function () {
                return this.undoSessionIndex;
            },


            /**
             * Setter function
             *
             * @param message
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setMessage: function (message) {
                this.message = message;
                return this;
            },

            /**
             * Getter function
             * returns text; no HTML!!
             *
             * @returns {string}
             */
            getMessage: function () {
                return this.message;
            },

            /**
             * Getter function for entry label;
             * can be implemented by each history entry to return html formatted message
             * @returns {string}
             */
            getLabel: function () {
                return this.message;
            },

            /**
             *
             * @param executionType
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setExecutionType: function setExecutionType(executionType) {
                this.executionType = executionType;
                return this;
            },

            getExecutionType: function getExecutionType() {
                return this.executionType;
            },

            /**
             *
             * @param entryType
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setEntryType: function setEntryType(entryType) {
                this.entryType = entryType;
                return this;
            },

            getEntryType: function getEntryType() {
                return this.entryType;
            },

            getPayload: function getPayload() {
                return this.payload;
            },
            /**
             *
             * @param payload
             * @returns {module.undoRedo.HistoryEntry.Base}
             */
            setPayload: function setPayload(payload) {
                this.payload = payload;
                return this;
            }


        });

        return HistoryEntry;
    }
);



/*****************
 ** WEBPACK FOOTER
 ** ./source/cdn/document_root/s/diy/undo-redo/historyEntry/Base.js
 ** module id = 900
 ** module chunks = 0
 **/